import React from 'react';
import { NavLink } from 'react-router-dom';
import type ColorScale from 'color-scales';
import type { IPhase } from '~/types/estimates';
import usePhaseDragReorder from '~/hooks/usePhaseDragReorder';
import tabStyles from './draggable-phase-tabs.module.scss';

type DraggablePhaseTabsProps = {
  phases: IPhase[];
  pathPrefix: string;
  getIsActive: (pathname: string) => boolean;
  isPreview: boolean;
  colorScale: ColorScale;
  tabDenominator: number;
  getPhaseLabel: (phase: IPhase, index: number) => string;
  onReorder: (phaseIds: string[]) => void | Promise<void>;
  layoutStyles: Record<string, string>;
  variant: 'tabs' | 'dropdown';
};

const DraggablePhaseTabs = ({
  phases,
  pathPrefix,
  getIsActive,
  isPreview,
  colorScale,
  tabDenominator,
  getPhaseLabel,
  onReorder,
  layoutStyles,
  variant,
}: DraggablePhaseTabsProps) => {
  const phaseIds = phases.map((phase) => phase._id);
  const { getTabDragProps, getTabDragStateClass } = usePhaseDragReorder({
    phaseIds,
    isPreview,
    onReorder,
  });

  if (variant === 'dropdown') {
    return (
      <>
        {phases.map((phase, index) => (
          <NavLink
            key={`phase-${phase._id}`}
            to={`${pathPrefix}/${phase._id}`}
            className={[
              layoutStyles.option,
              getTabDragStateClass(phase._id, tabStyles.isDragging, tabStyles.isDragOver),
            ].filter(Boolean).join(' ')}
            {...getTabDragProps(phase._id)}
          >
            {!isPreview && <span className={tabStyles.handle} aria-hidden>⋮⋮</span>}
            {getPhaseLabel(phase, index)}
          </NavLink>
        ))}
      </>
    );
  }

  return (
    <>
      {phases.map((phase, index) => (
        <NavLink
          key={`phase-${phase._id}`}
          to={`${pathPrefix}/${phase._id}`}
          className={getTabDragStateClass(phase._id, tabStyles.isDragging, tabStyles.isDragOver)}
          style={{
            position: 'relative',
            zIndex: phases.length - index + 100,
            boxShadow: getIsActive(`/${phase._id}`) ? 'none' : '0px 18px 32px rgba(208, 210, 218, 0.25)',
            backgroundColor: getIsActive(`/${phase._id}`)
              ? 'white'
              : colorScale.getColor((100 / tabDenominator) * (index + 1)).toRGBString(),
          }}
          {...getTabDragProps(phase._id)}
        >
          {!isPreview && <span className={tabStyles.handle} aria-hidden>⋮⋮</span>}
          {getPhaseLabel(phase, index)}
        </NavLink>
      ))}
    </>
  );
};

export default DraggablePhaseTabs;
